/**
 * Invoice Status Configuration
 * MUST match backend: customer-invoices/utils/statusConfig.js
 *
 * Database enum: ('Draft','Published','Partially_Paid','Paid','Overdue','Cancelled','Refunded')
 */

export const INVOICE_STATUSES = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  PARTIALLY_PAID: 'Partially_Paid',  // NEW - Added in migration 002
  PAID: 'Paid',
  OVERDUE: 'Overdue',
  CANCELLED: 'Cancelled',
  REFUNDED: 'Refunded'  // NEW - Added in migration 002
};

/**
 * Status colors for badges
 * Using Bootstrap color variants
 */
export const STATUS_COLORS = {
  [INVOICE_STATUSES.DRAFT]: 'secondary',           // Gray
  [INVOICE_STATUSES.PUBLISHED]: 'primary',         // Blue
  [INVOICE_STATUSES.PARTIALLY_PAID]: 'warning',    // Orange - NEW
  [INVOICE_STATUSES.PAID]: 'success',              // Green
  [INVOICE_STATUSES.OVERDUE]: 'danger',            // Red
  [INVOICE_STATUSES.CANCELLED]: 'secondary',       // Gray
  [INVOICE_STATUSES.REFUNDED]: 'info'              // Purple/Blue - NEW
};

/**
 * Human-readable status labels
 */
export const STATUS_LABELS = {
  [INVOICE_STATUSES.DRAFT]: 'Draft',
  [INVOICE_STATUSES.PUBLISHED]: 'Published',
  [INVOICE_STATUSES.PARTIALLY_PAID]: 'Partially Paid',  // NEW
  [INVOICE_STATUSES.PAID]: 'Paid',
  [INVOICE_STATUSES.OVERDUE]: 'Overdue',
  [INVOICE_STATUSES.CANCELLED]: 'Cancelled',
  [INVOICE_STATUSES.REFUNDED]: 'Refunded'  // NEW
};

/**
 * Get color for a status (for Badge component)
 * @param {string} status - Invoice status
 * @returns {string} Bootstrap color variant
 */
export const getStatusColor = (status) => {
  return STATUS_COLORS[status] || 'secondary';
};

/**
 * Get display label for a status
 * @param {string} status - Invoice status
 * @returns {string} Human-readable label
 */
export const getStatusLabel = (status) => {
  return STATUS_LABELS[status] || status;
};

/**
 * Get all statuses as array
 * @returns {Array<string>} All status values
 */
export const getAllStatuses = () => {
  return Object.values(INVOICE_STATUSES);
};

/**
 * Check if a status is valid
 * @param {string} status - Status to validate
 * @returns {boolean} True if valid
 */
export const isValidStatus = (status) => {
  return Object.values(INVOICE_STATUSES).includes(status);
};

/**
 * Status transitions (what statuses can change to)
 * Matches backend statusConfig.js
 */
export const STATUS_TRANSITIONS = {
  [INVOICE_STATUSES.DRAFT]: [
    INVOICE_STATUSES.PUBLISHED,
    INVOICE_STATUSES.CANCELLED
  ],
  [INVOICE_STATUSES.PUBLISHED]: [
    INVOICE_STATUSES.PARTIALLY_PAID,
    INVOICE_STATUSES.PAID,
    INVOICE_STATUSES.OVERDUE,
    INVOICE_STATUSES.CANCELLED,
    INVOICE_STATUSES.REFUNDED
  ],
  [INVOICE_STATUSES.PARTIALLY_PAID]: [
    INVOICE_STATUSES.PAID,
    INVOICE_STATUSES.OVERDUE,
    INVOICE_STATUSES.REFUNDED
  ],
  [INVOICE_STATUSES.PAID]: [
    INVOICE_STATUSES.REFUNDED
  ],
  [INVOICE_STATUSES.OVERDUE]: [
    INVOICE_STATUSES.PARTIALLY_PAID,
    INVOICE_STATUSES.PAID,
    INVOICE_STATUSES.REFUNDED,
    INVOICE_STATUSES.CANCELLED
  ],
  [INVOICE_STATUSES.CANCELLED]: [],
  [INVOICE_STATUSES.REFUNDED]: []
};

/**
 * Get allowed transitions for a status
 * @param {string} status - Current status
 * @returns {Array<string>} Allowed next statuses
 */
export const getAllowedTransitions = (status) => {
  return STATUS_TRANSITIONS[status] || [];
};
